import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';

import styles from './ConfirmDialog.module.scss';

export interface ConfirmDialogProps {
    isOpen: boolean;
    /** Closes without acting; also called once the action has succeeded. */
    onClose: () => void;
    title: ReactNode;
    /** The question, naming what is affected. */
    message: ReactNode;
    confirmLabel: string;
    /** `danger` for an action that destroys something; `primary` otherwise. */
    confirmTone?: 'danger' | 'primary';
    cancelLabel?: string;
    /**
     * The action. While its promise runs both buttons are locked and the dialog cannot be
     * closed; a rejection keeps it open and says why.
     */
    onConfirm: () => unknown;
    /** The error text when the failure carries no message of its own. */
    errorFallback?: string;
    confirmTrackingId?: string;
    /** Holds the confirm button back, e.g. while there is nothing to act on yet. */
    confirmDisabled?: boolean;
    /** Runs once the close transition has finished. */
    onClosed?: () => void;
}

/** Asks before an action, then runs it with the buttons locked and any error in the dialog. */
export default function ConfirmDialog(props: ConfirmDialogProps) {
    const {
        isOpen,
        onClose,
        title,
        message,
        confirmLabel,
        confirmTone = 'danger',
        cancelLabel,
        onConfirm,
        errorFallback,
        confirmTrackingId,
        confirmDisabled = false,
        onClosed,
    } = props;
    const { t } = useTranslation();
    const cancelRef = useRef<HTMLButtonElement>(null);
    const [pending, setPending] = useState(false);
    // The failure itself, so the alert can say why the server refused; `null` while none.
    const [failure, setFailure] = useState<{ error: unknown } | null>(null);

    useEffect(() => {
        if (isOpen) setFailure(null);
    }, [isOpen]);

    const close = () => {
        if (!pending) onClose();
    };

    const confirm = async () => {
        setFailure(null);
        setPending(true);
        try {
            await onConfirm();
            setPending(false);
            onClose();
        } catch (error: unknown) {
            setPending(false);
            setFailure({ error });
        }
    };

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={close}
            dismissible={!pending}
            centered={false}
            title={title}
            initialFocusRef={cancelRef}
            onClosed={onClosed}>
            <ModalBody>
                <div className={styles.body} aria-busy={pending}>
                    <p className={styles.message}>{message}</p>
                    {failure && (
                        <Alert tone="danger">
                            {apiErrorContent(
                                failure.error,
                                errorFallback ?? t('common.labels.somethingHappened')
                            )}
                        </Alert>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                {pending && (
                    <div className={styles.pending}>
                        <Spinner size="md" />
                    </div>
                )}
                <Button ref={cancelRef} variant="secondary" disabled={pending} onClick={close}>
                    {cancelLabel ?? t('common.actions.cancel')}
                </Button>
                <Button
                    variant={confirmTone}
                    disabled={pending || confirmDisabled}
                    trackingId={confirmTrackingId}
                    onClick={confirm}>
                    {confirmLabel}
                </Button>
            </ModalFooter>
        </ModalComponent>
    );
}

export { ConfirmDialog };
